import http from "./httpService";

/**
 * @typedef {Object} CreatePaymentIntentPayload
 * @property {number} cart_id
 * @property {number} count - Total bundle/makercrate quantity count
 * @property {string} currency
 */

/**
 * @typedef {Object} UpdatePaymentIntentPayload
 * @property {string} email
 * @property {string} phone
 * @property {string} country
 * @property {string} customer_name
 * @property {string} payment_intent_id
 * @property {number} shipping_cents - Shipping amount in USD (API field name)
 * @property {string} shipping_carrier
 * @property {string} shipping_service
 * @property {string} shipping_address
 * @property {string} shipping_address_additional
 * @property {string} shipping_city
 * @property {string} shipping_state
 * @property {string} shipping_zip
 * @property {string} shipping_country
 */

/**
 * @typedef {Object} StripePaymentIntentData
 * @property {string} clientSecret
 * @property {string} paymentIntentId
 */

/**
 * @typedef {Object} StripePaymentIntentResponse
 * @property {string} [message]
 * @property {string|null} [error]
 * @property {StripePaymentIntentData} [data]
 */

const SHIPPING_SERVICE_MAP = {
  ground: "FEDEX_GROUND",
  "2day": "FEDEX_2_DAY",
  overnight: "PRIORITY_OVERNIGHT",
};

/**
 * Build POST /stripe/payment-intent body.
 * @param {Object} params
 * @param {number|string} params.cartId
 * @param {number} params.count
 * @param {string} [params.currency]
 * @returns {CreatePaymentIntentPayload}
 */
export function buildCreatePaymentIntentPayload({
  cartId,
  count,
  currency = "usd",
}) {
  return {
    cart_id: Number(cartId),
    count: Math.max(1, Number(count) || 1),
    currency: currency || "usd",
  };
}

/**
 * Build PUT /stripe/payment-intent body.
 * @param {Object} params
 * @param {string} params.email
 * @param {string} [params.phone]
 * @param {string} params.country
 * @param {string} params.customerName
 * @param {string} params.paymentIntentId
 * @param {number} params.shippingPrice - USD dollars
 * @param {string} [params.shippingCarrier]
 * @param {string} [params.shippingService] - FedEx serviceType (e.g. FEDEX_GROUND)
 * @param {string} [params.shippingMethodId] - legacy local id fallback
 * @param {string} [params.shippingAddress]
 * @param {string} [params.shippingAddressAdditional]
 * @param {string} [params.shippingCity]
 * @param {string} [params.shippingState]
 * @param {string} [params.shippingZip]
 * @param {string} [params.shippingCountry]
 * @returns {UpdatePaymentIntentPayload}
 */
export function buildUpdatePaymentIntentPayload({
  email,
  phone = "",
  country,
  customerName,
  paymentIntentId,
  shippingPrice,
  shippingCarrier = "FedEx",
  shippingService,
  shippingMethodId,
  shippingAddress = "",
  shippingAddressAdditional = "",
  shippingCity = "",
  shippingState = "",
  shippingZip = "",
  shippingCountry = "",
}) {
  return {
    email: email || "",
    phone: phone || "",
    country: country || "United States",
    customer_name: customerName || "",
    payment_intent_id: paymentIntentId,
    shipping_cents: Math.max(0, Number(shippingPrice) || 0),
    shipping_carrier: shippingCarrier,
    shipping_service:
      shippingService ||
      SHIPPING_SERVICE_MAP[shippingMethodId] ||
      String(shippingMethodId || "").toUpperCase(),
    shipping_address: shippingAddress || "",
    shipping_address_additional: shippingAddressAdditional || "",
    shipping_city: shippingCity || "",
    shipping_state: shippingState || "",
    shipping_zip: String(shippingZip || "").trim(),
    shipping_country: shippingCountry || country || "United States",
  };
}

class StripePaymentService {
  /**
   * Create a Stripe Payment Intent.
   * @param {CreatePaymentIntentPayload} payload
   * @returns {Promise<StripePaymentIntentResponse>}
   */
  async createPaymentIntent(payload) {
    const response = await http.post("/stripe/payment-intent", payload);
    return response.data;
  }

  /**
   * Update an existing Payment Intent (e.g. after shipping selection).
   * @param {UpdatePaymentIntentPayload} payload
   * @returns {Promise<StripePaymentIntentResponse>}
   */
  async updatePaymentIntent(payload) {
    const response = await http.put("/stripe/payment-intent", payload);
    return response.data;
  }

  /**
   * Update the Payment Intent address from the Stripe payment section.
   * @param {UpdatePaymentIntentPayload} payload
   * @returns {Promise<StripePaymentIntentResponse>}
   */
  async updatePaymentIntentAddress(payload) {
    const response = await http.put("/stripe/payment-intent-address", payload);
    return response.data;
  }
}

export default new StripePaymentService();
